import { useEffect, useRef, useState } from "preact/hooks";
import { calibrateNoiseFloor, isMicSupported, requestMic, type MicSession } from "../audio/mic";

export type MicState = "idle" | "requesting" | "ready" | "denied" | "unsupported";

/** Acquires the mic once (when `enabled`), calibrates a noise-floor
    threshold, and releases everything on unmount/disable. Every consumer
    must treat `state !== "ready"` as "fall back to self-judge" — mic
    scoring is an enhancement, never a requirement. */
export function useMic(enabled: boolean) {
  const [state, setState] = useState<MicState>("idle");
  const [threshold, setThreshold] = useState(0.05);
  const micRef = useRef<MicSession | null>(null);
  const [, force] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setState("idle");
      return;
    }
    if (!isMicSupported()) {
      setState("unsupported");
      return;
    }
    let cancelled = false;
    setState("requesting");

    requestMic()
      .then(async (mic) => {
        if (cancelled) {
          mic.stop();
          return;
        }
        micRef.current = mic;
        force((n) => n + 1);
        const t = await calibrateNoiseFloor(mic);
        if (cancelled) {
          mic.stop();
          return;
        }
        setThreshold(t);
        setState("ready");
      })
      .catch(() => {
        if (!cancelled) setState("denied");
      });

    return () => {
      cancelled = true;
      micRef.current?.stop();
      micRef.current = null;
    };
  }, [enabled]);

  return { state, threshold, mic: micRef.current };
}
